import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { UploadCloud, Trash2, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { toast } from "sonner";

export const Route = createFileRoute("/_super/super/releases")({
  component: ReleasesPage,
});

type Platform = "macos" | "windows" | "linux";
const PLATFORM_LABEL: Record<Platform, string> = {
  macos: "macOS",
  windows: "Windows",
  linux: "Linux",
};

function formatSize(bytes?: number): string {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

function ReleasesPage() {
  const releases = useQuery(api.system.releases.queries.listReleases);
  const generateUploadUrl = useMutation(
    api.system.releases.mutations.generateUploadUrl,
  );
  const recordRelease = useMutation(
    api.system.releases.mutations.recordRelease,
  );
  const deleteRelease = useMutation(
    api.system.releases.mutations.deleteRelease,
  );

  const [platform, setPlatform] = useState<Platform>("macos");
  const [version, setVersion] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const onUpload = async () => {
    if (!file || !version.trim()) {
      toast.error("Completá versión y archivo");
      return;
    }
    setBusy(true);
    try {
      const uploadUrl = await generateUploadUrl();
      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload falló (${res.status})`);
      const { storageId } = (await res.json()) as { storageId: string };
      await recordRelease({
        platform,
        version: version.trim(),
        fileName: file.name,
        storageId: storageId as Id<"_storage">,
        fileSize: file.size,
      });
      toast.success(`Instalador ${PLATFORM_LABEL[platform]} ${version} subido`);
      setVersion("");
      setFile(null);
    } catch (err) {
      console.error(err);
      toast.error(`Error al subir: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label>Plataforma</Label>
              <Select
                value={platform}
                onValueChange={(v) => setPlatform(v as Platform)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="macos">macOS (.dmg)</SelectItem>
                  <SelectItem value="windows">Windows (.msi/.exe)</SelectItem>
                  <SelectItem value="linux">Linux (.AppImage/.deb)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="version">Versión</Label>
              <Input
                id="version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="0.5.0"
              />
            </div>
            <div>
              <Label htmlFor="file">Archivo</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <Button onClick={onUpload} disabled={busy} className="gap-2">
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <UploadCloud className="size-4" />
            )}
            {busy ? "Subiendo…" : "Subir instalador"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-3 text-sm font-semibold">Releases publicados</h3>
          {releases === undefined ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : releases.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Todavía no hay instaladores subidos.
            </p>
          ) : (
            <div className="divide-y">
              {releases.map((r) => (
                <div
                  key={r._id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {PLATFORM_LABEL[r.platform as Platform]} · v{r.version}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {r.fileName} · {formatSize(r.fileSize)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.url && (
                      <Button asChild variant="outline" size="icon">
                        <a href={r.url} target="_blank" rel="noopener noreferrer">
                          <Download className="size-4" />
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={async () => {
                        await deleteRelease({ id: r._id });
                        toast.success("Release eliminado");
                      }}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
